import { uuid } from '@/utils';
import { message, Upload, Image } from 'antd';
import { UploadFile } from 'antd/lib/upload/interface';
import React, { useEffect, useRef, useState } from 'react';
import PictureCrop from './picture-crop';
import './index.less';
import queryDomMethods from '@/utils/queryDomMethods';
import { useTranslation } from 'react-i18next';
import PlusOutlined from '@ant-design/icons/PlusOutlined';

interface IProps {
    imageList?: string[]; // 初始图片列表
    onChangeImage?: (images: string[]) => void;
    maxFileSize?: number; // 最大文件大小（单位：MB）
    allowedFileTypes?: string[]; // 允许的文件类型
    maxFileCount?: number; // 最大上传文件数量
}
const UploadImage: React.FC<IProps> = props => {
    const {
        imageList,
        onChangeImage,
        maxFileSize = 10, // 默认最大文件大小为 10MB
        allowedFileTypes = ['jpg', 'png'], // 默认允许的文件类型
        maxFileCount = 4, // 默认最大上传文件数量为 4
    } = props;

    const [isImagePreviewVisible, setIsImagePreviewVisible] = useState<boolean>(false);
    const [currentPreviewImage, setCurrentPreviewImage] = useState<UploadFile>();
    const [uploadFileList, setUploadFileList] = useState<UploadFile[]>([]);
    const [isCropVisible, setIsCropVisible] = useState(false);
    const [imageUrl, setImageUrl] = useState('');
    const fileNameRef = useRef('');
    const isInitUploadFileList = useRef(false);
    const { t } = useTranslation();

    useEffect(() => {
        if (imageList?.length && !isInitUploadFileList.current) {
            setUploadFileList(
                imageList.map(url => ({
                    uid: uuid(),
                    status: 'done',
                    name: url,
                    url: url,
                }))
            );
            isInitUploadFileList.current = true;
        }
    }, [imageList]);

    const beforeUpload = async (file: UploadFile) => {
        const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
        const isAllowedType = allowedFileTypes.includes(fileExtension);

        if (!isAllowedType) {
            message.warning(t('upload.typeLimit'));
            return false;
        }

        if (file.size! > maxFileSize * 1048576) {
            message.warning(t('upload.sizeLimit', { size: maxFileSize }));
            return false;
        }

        return true;
    };

    const handleUpload = async (info: any) => {
        const localUrl = URL.createObjectURL(info!.file as File);
        fileNameRef.current = info.name;
        setIsCropVisible(true);
        setImageUrl(localUrl);
    };

    const handleCropComplete = async (url: string) => {
        const newFileList: UploadFile[] = [
            ...uploadFileList,
            {
                uid: uuid(),
                status: 'done',
                name: fileNameRef.current,
                url: url,
            },
        ];

        setUploadFileList(newFileList);

        if (onChangeImage) {
            onChangeImage(newFileList.map(file => file.url!));
        }
    };

    const handlePreview = (file: UploadFile) => {
        setCurrentPreviewImage(file);
        setIsImagePreviewVisible(true);
    };

    const handleRemove = (file: UploadFile) => {
        const updatedFileList = uploadFileList.filter(item => item.uid !== file.uid);
        setUploadFileList(updatedFileList);

        if (onChangeImage) {
            onChangeImage(updatedFileList.map(item => item.url || ''));
        }
    };

    return (
        <div className="sub-tiku__image-upload-container">
            <Upload
                beforeUpload={beforeUpload}
                customRequest={handleUpload}
                fileList={uploadFileList}
                listType="picture-card"
                onPreview={handlePreview}
                onRemove={handleRemove}
            >
                {uploadFileList.length < maxFileCount && (
                    <div>
                        <PlusOutlined />
                        <div style={{ marginTop: 8 }}>{t('upload.uploadBtn')}</div>
                    </div>
                )}
            </Upload>
            {isImagePreviewVisible && (
                <Image
                    wrapperStyle={{ display: 'none' }}
                    preview={{
                        visible: true,
                        getContainer: queryDomMethods.querySubTikuRoot,
                        onVisibleChange: visible => setIsImagePreviewVisible(visible),
                        afterOpenChange: visible => !visible && setCurrentPreviewImage(undefined),
                    }}
                    src={currentPreviewImage?.url}
                />
            )}
            <PictureCrop visible={isCropVisible} url={imageUrl} onChange={handleCropComplete} />
        </div>
    );
};

export default UploadImage;
